import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useGetFullAlertAdmin } from './hooks/useAdminAlert';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PageTitle } from '@/components/PageTitle';
import {
  Calendar,
  Camera,
  ChevronDown,
  Clock,
  FileText,
  HardDrive,
  LinkIcon,
  Monitor,
  NotebookPen,
  RefreshCw,
  ShieldOff,
  Siren,
  SquareArrowOutUpRight,
  Table2,
  Tag,
  Volume2,
  XCircle,
} from 'lucide-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  addAlertNote,
  closeAlert,
  disarmAlert,
  EnumAlertStatus,
  updateAlertStatus,
} from './alertApi';
import { addAlertNote as addAlertNoteMonitoring } from '@/lib/api/monitoring/alert';
import { AdminEvent } from './components/AdminEvent';
import { AdminAlertDetails } from './components/AdminAlertDetails';
import { AdminAlertImageViewer } from './components/AdminAlertImageViewer';
import { AdminAlertEventList } from './components/AdminAlertEventList';
import { AdminAlertProvider, useAdminAlertContext } from './components/AdminAlertProvider';
import { sortEventsByDateAscending } from './utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { createUntagNoteText, formatEventTimeForTag } from '@/lib/tagging';
import { useContextualTimeZone } from '@/components/TimezoneProvider';

import { Loading } from '@/components/ui/loading';
import { Kbd } from '@/components/ui/kbd';
import { useAuthStore } from '@/store/authStore';
import { useRouteAccess } from '@/app/Roles/useRouteAccess';
import { MonitorAlertEventDto } from '@/app/Alert/types';
import { getGroupedNotesSorted } from '@/lib/alert/notes/group-notes';
import { TimezoneProvider } from '@/components/TimezoneProvider';

// Wrapper component that uses context to get selected event for the modal
function AdminEventModalWrapper({ 
  showEventModal, 
  setShowEventModal 
}: { 
  showEventModal: boolean; 
  setShowEventModal: (show: boolean) => void;
}) {
  const { selectedEvent } = useAdminAlertContext();
  
  if (!showEventModal || !selectedEvent) return null;
  
  return (
    <AdminEvent
      event={selectedEvent}
      open={showEventModal}
      onOpenChange={(open) => {
        if (!open) {
          setShowEventModal(false);
        }
      }}
    />
  );
}

function EventActionItems() {
  const { alert, selectedEvent, taggedEventIds, setShowTagDialog, setStartTimeOverrideMs, startTimeOverrideMs } = useAdminAlertContext();
  const { timeZone } = useContextualTimeZone();
  const taggingEnabled = useFeatureFlagEnabled(FeatureFlags.Tagging);
  const user = useAuthStore((state) => state.user);
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : 'Agent';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isSettingStartTime, setIsSettingStartTime] = useState(false);

  const isCurrentEventTagged = selectedEvent?.id ? taggedEventIds.has(selectedEvent.id) : false;

  const addTagMutation = useMutation({
    mutationFn: async (noteText: string) => {
      if (!alert?.id || !alert?.companyId || !alert?.projectId) throw new Error('Missing alert data');
      return addAlertNoteMonitoring({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: noteText,
        author: userFullName,
      });
    },
    onSuccess: () => {
      if (alert?.id) {
        queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
        queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      }
      toast({ title: 'Event Tagged', duration: 3000 });
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Failed to tag event', duration: 3000 });
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: async () => {
      if (!alert?.id || !alert?.companyId || !alert?.projectId || !selectedEvent?.id) throw new Error('Missing data');
      const eventTime = formatEventTimeForTag(selectedEvent.occurred, timeZone);
      return addAlertNoteMonitoring({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: createUntagNoteText({ eventId: selectedEvent.id, eventTime }),
        author: userFullName,
      });
    },
    onSuccess: () => {
      if (alert?.id) {
        queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
        queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      }
      toast({ title: 'Tag Removed', duration: 3000 });
    },
    onError: () => {
      toast({ variant: 'destructive', title: 'Failed to remove tag', duration: 3000 });
    },
  });

  const handleTagButtonClick = () => {
    if (!selectedEvent?.id) return;
    if (isCurrentEventTagged) {
      removeTagMutation.mutate();
    } else {
      setShowTagDialog(true);
    }
  };

  const handleSetStartTime = async () => {
    if (!selectedEvent?.occurred || !alert?.id || !alert?.companyId || !alert?.projectId) return;
    const eventMs = new Date(selectedEvent.occurred).getTime();
    const isAlreadySet = startTimeOverrideMs !== null && eventMs === startTimeOverrideMs;

    setIsSettingStartTime(true);
    setStartTimeOverrideMs(isAlreadySet ? null : eventMs);
    try {
      const overrideNote = isAlreadySet
        ? '[START_TIME_OVERRIDE]NONE'
        : `[START_TIME_OVERRIDE]${new Date(selectedEvent.occurred).toISOString()}`;
      await addAlertNoteMonitoring({
        alertId: alert.id,
        companyId: alert.companyId,
        projectId: alert.projectId,
        note: overrideNote,
        author: userFullName,
      });
      queryClient.invalidateQueries({ queryKey: ['fullAlert', alert.id] });
      queryClient.invalidateQueries({ queryKey: ['alert', alert.id] });
      toast({
        title: isAlreadySet ? 'Start time removed' : 'Start time updated',
        description: isAlreadySet ? undefined : 'Incident start time has been set to this event.',
        duration: 3000,
      });
    } catch {
      setStartTimeOverrideMs(isAlreadySet ? startTimeOverrideMs : null);
      toast({ variant: 'destructive', title: `Failed to ${isAlreadySet ? 'remove' : 'set'} start time`, duration: 3000 });
    } finally {
      setIsSettingStartTime(false);
    }
  };

  if (!taggingEnabled) return null;

  return (
    <>
      <DropdownMenuItem
        onClick={handleTagButtonClick}
        disabled={!selectedEvent || addTagMutation.isPending || removeTagMutation.isPending}
        className={isCurrentEventTagged ? 'text-lime-500' : ''}
      >
        <Tag className={`h-4 w-4 ${isCurrentEventTagged ? 'fill-lime-500' : ''}`} />
        {isCurrentEventTagged ? 'Untag Event' : 'Tag Event'}
        <span className="ml-auto"><Kbd size="sm">t</Kbd></span>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={handleSetStartTime}
        disabled={!selectedEvent || isSettingStartTime}
      >
        <Clock className="h-4 w-4" />
        {isSettingStartTime ? 'Setting...' : 'Set Start Time'}
        <span className="ml-auto"><Kbd size="sm">r</Kbd></span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
    </>
  );
}

export default function AdminAlert() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const { hasAccessToRoute } = useRouteAccess();
  const quillAuditToolFlag = useFeatureFlagEnabled('quill-audit-tool');
  // const { data: alert, isLoading, error, refetch: refetchAlert } = useGetAlert(alertId!);
  const { data: alert, isLoading, error, refetch: refetchAlert } = useGetFullAlertAdmin(alertId);
  const [disarmDialog, setDisarmDialog] = useState(false);
  const [closeDialog, setCloseDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [addNoteDialog, setAddNoteDialog] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [resolution, setResolution] = useState('');
  const [disarmReason, setDisarmReason] = useState('');
  const [status, setStatus] = useState<string>('');
  const [showEventModal, setShowEventModal] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
 
  const hasQuillAnalysis =
    alert?.recentNotes?.some((n) => (n as any).metadata?.type === 'QuillAnalysis') || false;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Loading />
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <div className="rounded-lg bg-yellow-500/10 p-6 text-center">
          <h2 className="mb-2 text-xl font-semibold text-yellow-500">Alert Not Found</h2>
          <p className="mb-4 text-gray-300">The requested alert could not be found.</p>
          <button
            onClick={() => navigate('/alerts')}
            className="rounded bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700"
          >
            Return to Alerts
          </button>
        </div>
      </div>
    );
  }

  return (
    <TimezoneProvider timeZone={alert.project?.timeZone} label="Site timezone" entityName={alert.project?.name}>
      <AdminAlertProvider alert={alert}>
    <div className="flex h-full flex-col">
      <PageTitle title={`${alert.alertId}`} />
      <div className="flex items-center justify-between gap-2 border-b bg-[#111] p-2">
        <div className="flex items-center gap-2 pl-1.5">
          <Siren className="-mt-0.5 h-5 w-5" />
          <h1 className="font-semibold">Alert Review - {alert.alertId}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAddNoteDialog(true)}>
            <NotebookPen className="h-4 w-4" />
            Add Note
          </Button>
          <Button variant="outline" onClick={() => window.open(`/monitoring/alert/${alert.id}/report`, '_blank')}>
            <FileText className="h-4 w-4" />
            View Report
          </Button>
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              Alert Actions
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Links</DropdownMenuLabel>
            {hasAccessToRoute('/monitor') && (
              <DropdownMenuItem onClick={() => window.open(`/alert/${alertId}`, '_blank')}>
                <Monitor className="h-4 w-4" />
                View in Portal
              </DropdownMenuItem>
            )}
            {quillAuditToolFlag && (
              <DropdownMenuItem onClick={() => navigate(`/alert-audit?alertId=${alert.alertId}`)}>
                <SquareArrowOutUpRight className="h-4 w-4" />
                Quill Audit
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <EventActionItems />
            {alert.status !== EnumAlertStatus.Unknown && (
              <DropdownMenuItem
                disabled={!!alert.disarmedAt}
                onClick={() => setDisarmDialog(true)}
              >
                <ShieldOff className="h-4 w-4" />
                Disarm
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={!!alert.closedAt}
              onClick={() => setCloseDialog(true)}
            >
              <XCircle className="h-4 w-4" />
              Close Alert
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setStatusDialog(true)}>
              <RefreshCw className="h-4 w-4" />
              Update Status
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowEventModal(true)}>
              <Table2 className="h-4 w-4" />
              Update Grid
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>

        {/* Add Note Dialog */}
        <Dialog open={addNoteDialog} onOpenChange={setAddNoteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Note</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-500">
                Any notes put here will be visible to the customer and monitoring company
              </p>
              <Textarea
                placeholder="Note"
                value={noteInput}
                onChange={(e) => setNoteInput(e.target.value)}
                required
              />
              <Button
                disabled={!noteInput.trim()}
                onClick={async () => {
                  if (noteInput.trim()) {
                    await addAlertNote(alertId!, {
                      note: noteInput,
                      author: authStore.user?.firstName + ' ' + authStore.user?.lastName,
                      nonActioningNote: true,
                    });
                    setNoteInput('');
                    setAddNoteDialog(false);
                    await refetchAlert();
                  }
                }}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Disarm Dialog */}
        <Dialog open={disarmDialog} onOpenChange={setDisarmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Disarm System For this Alert</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-500">
                Alerts will stay open until there is a period of{' '}
                {alert.project?.newMonitoringAlertsAfterSeconds} seconds without events.
                Disarming will disable sirens until a new alert is created.
              </p>
              <Textarea
                placeholder="Reason"
                value={disarmReason}
                onChange={(e) => setDisarmReason(e.target.value)}
                required
              />
              <Button
                disabled={!disarmReason.trim()}
                onClick={async () => {
                  await disarmAlert(alertId!, {
                    reason: disarmReason || 'Disarmed by user',
                    disarmedBy: authStore.user?.firstName + ' ' + authStore.user?.lastName,
                  });
                  setDisarmReason('');
                  setDisarmDialog(false);
                  await refetchAlert();
                }}
              >
                Disarm System
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Close Alert Dialog */}
        <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Close this Alert</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-500">
                Alerts can be closed once a resolution has been reached. The alert may
                automatically be re-opened if an event arrives within{' '}
                {alert.project?.newMonitoringAlertsAfterSeconds} seconds of the last event
              </p>
              <Textarea
                placeholder="Resolution"
                required
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
              />
              <Button
                disabled={!resolution.trim()}
                onClick={async () => {
                  await closeAlert(alertId!, {
                    alertId: alertId!,
                    closingAuthor: authStore.user?.firstName + ' ' + authStore.user?.lastName,
                    resolution: resolution,
                  });
                  setCloseDialog(false);
                  await refetchAlert();
                }}
              >
                Close Alert
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Update Status Dialog */}
        <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Alert Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 p-4">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(EnumAlertStatus).map((statusValue) => (
                    <SelectItem key={statusValue} value={statusValue}>
                      {statusValue}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                disabled={!status}
                onClick={async () => {
                  if (
                    status &&
                    Object.values(EnumAlertStatus).includes(status as EnumAlertStatus)
                  ) {
                    await updateAlertStatus(alertId!, {
                      setBy: authStore.user?.firstName + ' ' + authStore.user?.lastName,
                      status: status as EnumAlertStatus,
                    });
                    setStatus('');
                    setStatusDialog(false);
                    await refetchAlert();
                  }
                }}
              >
                Update
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid h-[calc(100vh-113px)] grid-cols-[400px_1fr_400px] overflow-hidden">
            <AdminAlertEventList />
            <AdminAlertImageViewer
              setShowEventModal={setShowEventModal}
              imageRef={imageRef}
            />
            <AdminAlertDetails
              alert={alert}
              refetchAlert={refetchAlert}
            />
            <AdminEventModalWrapper 
              showEventModal={showEventModal} 
              setShowEventModal={setShowEventModal} 
            />
      </div>
    </div>
      </AdminAlertProvider>
    </TimezoneProvider>
  );
}
